<template>
  <div class="main-container login-page">
    <!-- Left: Form -->
    <div class="login-form-container">
      <div class="login-form-content">
        <h2>{{ t('login.welcome') }}</h2>
        <div class="logo-text">
          <span>{{ logoText }}</span>
        </div>
        <form class="login-form" @submit.prevent="handleLogin">
          <div class="input-field username">
            <label for="username" class="visually-hidden">{{ t('login.usernamePlaceholder') }}</label>
            <IconUser class="icon-user" width="18" height="20" />
            <input id="username" v-model="username" :placeholder="t('login.usernamePlaceholder')"
              autocomplete="username" required />
            <div v-if="errors.username" class="error-message">{{ errors.username }}</div>
          </div>
          <div class="password-input">
            <button type="button" class="password-show-hide" @click="togglePassword"
              :aria-label="showPassword ? t('login.hide') : t('login.show')" :aria-pressed="showPassword">
              <IconEye v-if="!showPassword" width="16" height="12" />
              <IconEyeOff v-if="showPassword" width="16" height="14" />
              <span>{{ showPassword ? t('login.hide') : t('login.show') }}</span>
            </button>
            <div class="input-field password">
              <label for="password" class="visually-hidden">{{ t('login.passwordPlaceholder') }}</label>
              <IconLock class="icon-lock" width="16" height="18" />
              <input id="password" v-model="password" :type="showPassword ? 'text' : 'password'"
                :placeholder="t('login.passwordPlaceholder')" autocomplete="current-password" required />
              <div class="forgot-password">
                <a href="javascript:void(0)">{{ t('login.forgotPassword') }}</a>
              </div>
              <div v-if="errors.password" class="error-message">{{ errors.password }}</div>
            </div>
          </div>
          <button type="submit" class="btn btn-blue" :disabled="loading">
            <span v-if="loading" class="spinner" :aria-label="t('login.loading')"></span>
            {{ t('login.login') }}
          </button>
        </form>
      </div>
    </div>
    <!-- Right: Image -->
    <div class="right-background">
      <img src="@/assets/images/login-bg-image.png" :alt="t('login.loginBackground')" />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { apiService } from '@/services/apiService';
import IconEye from '@/components/icons/iconEye.vue';
import IconEyeOff from '@/components/icons/iconEyeOff.vue';
import IconUser from '@/components/icons/iconUser.vue';
import IconLock from '@/components/icons/iconLock.vue';

const router = useRouter();
const { t } = useI18n();
const logoText = process.env.VUE_APP_LOGO_TEXT;

const username = ref('');
const password = ref('');
const showPassword = ref(false);
const loading = ref(false);
const errors = ref({ username: '', password: '' });

const handleLogin = async () => {
  loading.value = true;
  errors.value = { username: '', password: '' };
  try {
    await apiService.login(username.value, password.value);
    router.push('/');
  } catch (error) {
    const data = error?.response?.data;
    if (typeof data?.detail === 'string') {
      errors.value.username = data.detail;
    } else {
      errors.value.password = t('login.errorGeneric');
    }
  } finally {
    loading.value = false;
  }
};

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};
</script>

<style scoped>
.password-show-hide {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  justify-content: flex-end;
  color: var(--charcoal-black-5);
  
}

.error-message {
  color: #e74c3c;
  font-size: 0.8em;
  margin-top: 4px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid #fff;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5em;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>